/**
 * The DB grid's shared vocabulary: the constants both grids and the tab agree
 * on, and how a cell is addressed.
 *
 * Its own module because the grids moved out of DbManagerResultsTab and would
 * otherwise have to import them back from it — a cycle, and a reason for the
 * grids to keep knowing about the tab.
 */

// Rows past this are not addressable. It used to be a DOM-size cap too — a
// capped 10,000-row result is 10,000 x N <td>s — but the grid now virtualizes,
// so only a windowful is ever in the document. It survives as the FETCH cap
// (loadMoreRows stops here) and as the ceiling the find-in-results search is
// bounded by, both of which are still real limits.
export const MAX_RENDER_ROWS = 10000;

/**
 * One row's height in px.
 *
 * `.dbm-td` is `height: 36px` with `box-sizing: border-box` — which already
 * INCLUDES its 1px bottom border — and `.dbm-table` is `border-spacing: 0`, so
 * the row box is exactly 36. (It was briefly 37 here, on the reasoning that the
 * border adds to the box. `box-sizing: border-box` is precisely the rule that
 * makes that false, and the 1px-per-row drift it caused was being hidden by
 * per-row measurement — which cost a layout read per row per scroll and was the
 * jank it looked like it was preventing.)
 *
 * Still only a starting value: the grid measures ONE real row after mount and
 * uses that, so a restyle costs a single reflow rather than silent drift.
 */
export const ROW_HEIGHT_ESTIMATE = 36;

/**
 * Below this many rows the grid renders every row and does not virtualize.
 *
 * Virtualizing is not free: the virtualizer re-runs on every scroll event and
 * re-renders the grid each time the window moves a row, where an un-windowed
 * table scrolls with no React work at all. That trade only pays once the DOM
 * it saves is bigger than the renders it adds — a 36-row table was paying the
 * cost for nothing, which is what "scrolling got less smooth" was.
 */
export const VIRTUALIZE_MIN_ROWS = 200;

/**
 * How long Ctrl+F waits after the last keystroke before searching.
 *
 * A search is rows × columns string comparisons across up to MAX_RENDER_ROWS,
 * which is fine once and not fine on every keydown of a word typed at speed.
 * Long enough to swallow a fast typist's inter-key gap, short enough that a
 * deliberate pause feels like it answered immediately.
 */
export const FIND_DEBOUNCE_MS = 140;

// Stable empty values, so a default prop is the same object every render and
// does not retrigger memoisation downstream.
export const EMPTY_COLUMN_WIDTHS = {};
export const EMPTY_FIND_KEYS = new Set();
export const EMPTY_HIDDEN_COLUMNS = new Set();
export const EMPTY_SORT_LEVELS = [];
export const EMPTY_PENDING_ROWS = [];
export const EMPTY_PENDING_EDITS = {};
export const EMPTY_PENDING_DELETES = new Set();

// The row-number column's width lives in the same map as the real columns, so
// it needs a key no column name can collide with. Under `table-layout: fixed`
// an unsized <col> gets whatever is left over — which is zero once every other
// column is pinned, so the column vanished on first resize until this existed.
export const ROWNUM_WIDTH_KEY = '\u0000rownum';

/**
 * Cell addresses span two spaces: saved result rows are addressed by their
 * index into result.rows, while not-yet-inserted pending rows are addressed by
 * their (index-independent) temp id. The kind tag keeps the two apart in one
 * Set.
 */
export function cellKey(kind, id, col) {
  return `${kind}:${id}:${col}`;
}

export function parseCellKey(key) {
  const [kind, id, col] = key.split(':');
  return { kind, id: Number(id), col: Number(col) };
}

/**
 * Split pending (not yet inserted) rows into "belongs under saved row N" and
 * "belongs at the bottom".
 *
 * A duplicate carries `afterRow`, the index of the row it was copied from, so
 * it can be drawn directly beneath it — a copy that appears a thousand rows
 * away from its original is hard to relate to what you just did. A row from the
 * Add row button has no source and stays at the end, which is where "add" means.
 *
 * `afterRow` is an index into the CURRENT result set, exactly like the keys in
 * pendingEdits, and any refetch that replaces the rows invalidates it.
 *
 * What protects it is a GUARD, not a clear — do not assume the clear. Sort,
 * filter, page-size and refresh all refuse to run while `hasUnsavedGridChanges`,
 * which counts `pendingRows`; `runTableQuery` itself clears `pendingEdits` and
 * leaves `pendingRows` alone. A future refetch path that forgets the guard gets
 * no error out of this: a stale `afterRow` draws the copy under the wrong row,
 * quietly.
 */
export function groupPendingRows(pendingRows) {
  const under = new Map();
  const trailing = [];
  (pendingRows || []).forEach((pRow) => {
    if (!Number.isInteger(pRow.afterRow)) {
      trailing.push(pRow);
      return;
    }
    const bucket = under.get(pRow.afterRow);
    if (bucket) bucket.push(pRow);
    else under.set(pRow.afterRow, [pRow]);
  });
  return { under, trailing };
}
